import dayjs from "dayjs";

// A connection can stay fully authorized while the bank or accounting system
// quietly stops sending data. Plaid's own item status only describes the OAuth
// connection, and lastSyncAt/pullDate move on every sync attempt -- including
// one that returned nothing -- so neither can answer "is data still arriving".
// lastTransactionAt is the only field that moves when transactions actually
// land, and it is what STALE_DAYS is measured against here.
export const STALE_DAYS = 30;

// A connection linked moments ago has legitimately imported nothing yet. It
// should read as still-connecting rather than immediately accusing itself.
export const NEW_CONNECTION_GRACE_DAYS = 7;

const LEVELS = {
  error: "error",
  accountsRemoved: "accountsRemoved",
  stale: "stale",
  neverDelivered: "neverDelivered",
  connecting: "connecting",
  healthy: "healthy"
};

// An Item that has stopped sharing an account is still fully authorized and
// still delivering data for everything else, so it reports no error and looks
// entirely healthy by every other measure here.
//
// Whether that matters depends entirely on why the account went away. An
// account closed at the bank has nothing left to give and nothing is missing;
// one that was still delivering when it vanished is a real gap; one that never
// delivered anything at all is not evidence it was closed -- a brand-new
// account can be de-selected before its first sync ever runs. The backend
// records which, per account, at the moment it disappeared.
//
// Named for the one cause that means nothing is missing, not for the several
// that do: everything else -- the known attention-worthy causes AND any value
// this list doesn't recognize (a future backend addition, a rolling deploy
// briefly mismatched between front and back, a malformed doc) -- is treated
// as needing attention by default. Failing toward a warning for an
// unrecognized cause is deliberate; this whole feature exists so this file
// never tells a customer nothing is missing when it can't actually tell.
const CLOSED_CAUSES = ["likely-closed"];

const removedAccounts = ({ accountReconciliation }) => {
  const { state, removedAccountIds, removedAccountDetails } =
    accountReconciliation || {};

  if (state !== "accounts-removed") {
    return [];
  }

  // Docs written before causes were recorded carry ids only. Treating those as
  // unknown keeps an existing warning standing rather than silently clearing it
  // the moment this ships.
  return (
    removedAccountDetails ||
    (removedAccountIds || []).map((accountId) => ({
      accountId,
      cause: "unknown"
    }))
  );
};

const isClosed = ({ cause }) => CLOSED_CAUSES.includes(cause);

const accountsNeedingAttention = (integration) =>
  removedAccounts(integration).filter((detail) => !isClosed(detail));

// Firestore timestamps, JS Dates and ISO strings all reach the UI depending on
// whether a value came from a listener, a cache or a backfill.
const toDate = (value) => {
  if (!value) {
    return null;
  }
  if (value.toDate) {
    return value.toDate();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const daysSince = (value, now) => {
  const date = toDate(value);
  return date ? dayjs(now).diff(dayjs(date), "day") : null;
};

const formatDate = (value) => {
  const date = toDate(value);
  return date ? dayjs(date).format("MMMM D, YYYY") : null;
};

// Accounts that stopped being shared with nothing left to collect -- closed at
// the bank, or never delivered a transaction in the first place. Worth telling
// the customer so a disappearing account is never a silent surprise, but it is
// not a problem and there is nothing for them to do about it.
export const closedAccountsNote = (integration = {}) => {
  const closed = removedAccounts(integration).filter(isClosed);

  if (!closed.length) {
    return null;
  }

  const lastActivity = closed
    .map(({ lastTransactionAt }) => toDate(lastTransactionAt))
    .filter(Boolean)
    .sort((a, b) => b - a)[0];

  return {
    count: closed.length,
    lastActivityLabel: lastActivity ? formatDate(lastActivity) : null
  };
};

// Plaid's own error signature, unchanged from what the settings cards checked
// before staleness existed as a concept.
export const hasIntegrationError = (integration = {}) =>
  Boolean(
    integration.status === "error" &&
    integration.errorCode &&
    !integration.revokedAt &&
    !integration.archived
  );

const isQuickBooks = ({ name, type }) =>
  name === "quickbooks" || type === "quickbooks";

const hasExpiredRefreshToken = (integration, now) =>
  Boolean(
    isQuickBooks(integration) &&
    integration.refreshTokenExpiration &&
    dayjs(now).isAfter(dayjs(toDate(integration.refreshTokenExpiration)))
  );

const describeStaleness = (days) => {
  const months = Math.floor(days / 30);
  if (months >= 2) {
    return `${months} months`;
  }
  return `${days} days`;
};

/**
 * Derives what we can honestly say about one integration's health.
 *
 * `level` is one of:
 *   error          - needs reauthentication, or the provider reported a failure
 *   accountsRemoved - authorized, but an account it used to share is now absent
 *   stale          - authorized and error-free, but nothing in STALE_DAYS
 *   neverDelivered - authorized past its grace window, has never imported once
 *   connecting     - linked within the grace window and nothing imported yet
 *   healthy        - transactions arrived recently
 *
 * A missing lastTransactionAt means one of two different things, and they must
 * not be conflated: either nothing has ever arrived, or the doc predates the
 * field and has not been backfilled. transactionCount is what separates them --
 * the sync writes it cumulatively, so an explicit 0 is positive evidence that
 * nothing ever landed, while a positive count or an absent field means we simply
 * do not know yet. Unknown stays healthy, because treating it as a problem would
 * warn on every pre-existing connection until the backfill runs.
 */
export const connectionHealth = (integration = {}, now = new Date()) => {
  const {
    lastTransactionAt,
    createdAt,
    transactionCount,
    doesRequireReauthentication
  } = integration;

  if (doesRequireReauthentication || hasExpiredRefreshToken(integration, now)) {
    return {
      level: LEVELS.error,
      label: "Reconnection needed",
      tooltip:
        "This connection needs to be reconnected before transactions can import again.",
      daysSinceLastTransaction: daysSince(lastTransactionAt, now)
    };
  }

  if (hasIntegrationError(integration)) {
    return {
      level: LEVELS.error,
      label: "Connection error",
      tooltip: "Sync error - Click for more information",
      daysSinceLastTransaction: daysSince(lastTransactionAt, now)
    };
  }

  // Deliberately ranked above staleness: an account that stopped being shared
  // explains the missing data, and saying "no transactions in 2 months" instead
  // would send the customer to reconnect without telling them the one thing
  // they have to do differently when they get there -- re-select the account.
  const removedCount = accountsNeedingAttention(integration).length;
  if (removedCount) {
    return {
      level: LEVELS.accountsRemoved,
      label: `${removedCount} ${
        removedCount > 1 ? "accounts are" : "account is"
      } no longer being shared`,
      tooltip:
        "This connection is authorized, but the bank has stopped sending us data for at least one account - click for details",
      daysSinceLastTransaction: daysSince(lastTransactionAt, now)
    };
  }

  const daysSinceLastTransaction = daysSince(lastTransactionAt, now);
  const daysSinceCreated = daysSince(createdAt, now);
  const isWithinGrace =
    daysSinceCreated !== null && daysSinceCreated <= NEW_CONNECTION_GRACE_DAYS;

  if (daysSinceLastTransaction === null) {
    if (isWithinGrace) {
      return {
        level: LEVELS.connecting,
        label: "Importing transactions",
        tooltip: "We're still importing this connection's first transactions.",
        daysSinceLastTransaction: null
      };
    }
    // Past the grace window with a sync that has cumulatively imported exactly
    // nothing. Usually means no eligible accounts were selected when the
    // connection was linked, so it has been sitting inert since day one.
    if (transactionCount === 0) {
      return {
        level: LEVELS.neverDelivered,
        label: "No transactions have ever imported",
        tooltip:
          "This connection is authorized but has never sent us a transaction - click for details",
        daysSinceLastTransaction: null
      };
    }
    return {
      level: LEVELS.healthy,
      label: "Connected",
      tooltip: "",
      daysSinceLastTransaction: null
    };
  }

  if (daysSinceLastTransaction > STALE_DAYS) {
    return {
      level: LEVELS.stale,
      label: `No transactions in ${describeStaleness(daysSinceLastTransaction)}`,
      tooltip: `No transactions imported since ${formatDate(
        lastTransactionAt
      )} - click for details`,
      daysSinceLastTransaction
    };
  }

  return {
    level: LEVELS.healthy,
    label: "Connected",
    tooltip: "",
    daysSinceLastTransaction
  };
};

// A connection that is authorized and error-free but is not delivering all the
// data it should -- it stopped, it never started, or it is still delivering for
// every account except the ones it no longer shares. All three warrant the same
// amber warning and the same "this is not an error, and here is what to do"
// explanation; the copy differs, which is what `label` is for.
export const hasDataGap = (integration, now) =>
  [LEVELS.stale, LEVELS.neverDelivered, LEVELS.accountsRemoved].includes(
    connectionHealth(integration, now).level
  );

/**
 * The clause a settings card appends to "Connected N institutions" when
 * something needs attention. Errors and staleness are counted separately
 * because a stale connection reports no error at all -- which is precisely why
 * a card that only counted errors said nothing was wrong.
 *
 * Returns "" when every connection is fine.
 */
export const connectionProblemsSummary = (integrations = [], now) => {
  const levels = integrations.map(
    (integration) => connectionHealth(integration, now).level
  );
  const countOf = (level) => levels.filter((value) => value === level).length;

  const errored = countOf(LEVELS.error);
  const stale = countOf(LEVELS.stale);
  const neverDelivered = countOf(LEVELS.neverDelivered);
  const accountsRemoved = countOf(LEVELS.accountsRemoved);

  const clauses = [
    errored > 0 &&
      `${errored} ${errored > 1 ? "have errors" : "has errors"} that need attention`,
    accountsRemoved > 0 &&
      `${accountsRemoved} ${
        accountsRemoved > 1 ? "have accounts" : "has an account"
      } that ${accountsRemoved > 1 ? "are" : "is"} no longer being shared`,
    stale > 0 &&
      `${stale} ${
        stale > 1 ? "haven't" : "hasn't"
      } imported transactions in over ${STALE_DAYS} days`,
    neverDelivered > 0 &&
      `${neverDelivered} ${
        neverDelivered > 1 ? "have" : "has"
      } never imported any transactions`
  ].filter(Boolean);

  return clauses.join(", and ");
};

export const lastTransactionLabel = (integration = {}) =>
  formatDate(integration.lastTransactionAt) || "No transactions imported yet";
